import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, getDoc, runTransaction } from 'firebase/firestore';
import { db, auth } from '../firebase/config';

// Format currency in ZAR
// eslint-disable-next-line no-unused-vars
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount);
};

export default function CreateReceipt() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const invoiceId = queryParams.get('invoiceId');
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    clientPhone: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    invoiceNumber: '',
    amount: 0,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoClick = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Get next receipt number from counter
      let receiptNumber;
      await runTransaction(db, async (transaction) => {
        const counterRef = doc(db, 'counters', 'receipts');
        const counterDoc = await transaction.get(counterRef);
        
        let nextNumber = 1;
        if (counterDoc.exists()) {
          nextNumber = counterDoc.data().count + 1;
        }
        
        transaction.set(counterRef, { count: nextNumber });
        receiptNumber = `RCT-${nextNumber.toString().padStart(6, '0')}`;
      });
      
      // Check if user is authenticated
      if (!auth.currentUser) {
        console.error('No authenticated user found');
        setLoading(false);
        return;
      }
      
      const receiptData = {
        ...formData,
        receiptNumber,
        userId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        paymentDate: formData.paymentDate ? new Date(formData.paymentDate) : null,
        logoBase64: logo,
        // Ensure both naming conventions are included
        customerName: formData.clientName,
        customerEmail: formData.clientEmail,
        customerAddress: formData.clientAddress,
        customerPhone: formData.clientPhone,
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        clientAddress: formData.clientAddress,
        clientPhone: formData.clientPhone
      };
      
      console.log('Attempting to save receipt with data:', receiptData);
      
      const docRef = await addDoc(collection(db, 'receipts'), receiptData);
      console.log('Receipt saved successfully with ID:', docRef.id);
      
      navigate('/receipts');
    } catch (error) {
      console.error('Error creating receipt:', error);
      setLoading(false);
    }
  };

// Add this useEffect to fetch invoice data if invoiceId is provided
useEffect(() => {
  const fetchInvoiceData = async () => {
    if (invoiceId) {
      setLoading(true);
      try {
        const invoiceRef = doc(db, 'invoices', invoiceId);
        const invoiceSnap = await getDoc(invoiceRef);
        
        if (invoiceSnap.exists()) {
          const invoiceData = invoiceSnap.data();
          
          // Pre-fill form with invoice data
          setFormData({
            clientName: invoiceData.clientName || invoiceData.customerName || '',
            clientEmail: invoiceData.clientEmail || invoiceData.customerEmail || '',
            clientAddress: invoiceData.clientAddress || invoiceData.customerAddress || '',
            clientPhone: invoiceData.clientPhone || invoiceData.customerPhone || '',
            companyName: invoiceData.companyName || '',
            companyEmail: invoiceData.companyEmail || '',
            companyAddress: invoiceData.companyAddress || '',
            companyPhone: invoiceData.companyPhone || '',
            amount: invoiceData.total || 0,
            paymentDate: new Date().toISOString().split('T')[0],
            paymentMethod: 'bank_transfer',
            invoiceNumber: invoiceData.invoiceNumber || invoiceData.invoiceNo || '',
            notes: `Payment for invoice #${invoiceData.invoiceNumber || invoiceData.invoiceNo}`
          });
          
          // Set logo if available
          if (invoiceData.logoBase64) {
            setLogo(invoiceData.logoBase64);
          }
        }
      } catch (error) {
        console.error('Error fetching invoice data:', error);
      } finally {
        setLoading(false);
      }
    }
  };
  
  fetchInvoiceData();
}, [invoiceId]);

return (
    <div className="form-container">
      <div className="form-section">
        <h1 className="form-title">Create Payment Receipt</h1>
        
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2 className="form-section-title">Your Company Information</h2>
            <div className="form-logo-section">
              <div 
                className="logo-upload-area" 
                onClick={handleLogoClick}
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Company Logo" className="logo-preview" />
                ) : (
                  <div className="logo-placeholder">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 22h16a2 2 0 0 0 2-2V8l-6-6H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2z"></path>
                      <path d="M14 2v6h6"></path>
                      <circle cx="10" cy="13" r="2"></circle>
                      <path d="M10 9v2"></path>
                      <path d="M10 15v2"></path>
                      <path d="M14 13h2"></path>
                      <path d="M6 13h2"></path>
                    </svg>
                    <span>Click to add logo</span>
                  </div>
                )}
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleLogoChange} 
                  accept="image/*" 
                  className="hidden-file-input" 
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2 className="form-section-title">Client Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Client Name</label>
                <input
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Client Name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Client Email</label>
                <input
                  type="email"
                  name="clientEmail"
                  value={formData.clientEmail}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Client Email"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Client Phone</label>
                <input
                  type="text"
                  name="clientPhone"
                  value={formData.clientPhone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Client Phone"
                />
              </div>
              <div className="form-group form-grid-full">
                <label className="form-label">Client Address</label>
                <textarea
                  name="clientAddress"
                  value={formData.clientAddress}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="2"
                  placeholder="Client Address"
                ></textarea>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2 className="form-section-title">Payment Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Invoice Number (Optional)</label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Related Invoice Number"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Date</label>
                <input
                  type="date"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  required
                  className="form-input"
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Amount Paid</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="form-input"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2 className="form-section-title">Additional Notes</h2>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="form-textarea"
              rows="3"
              placeholder="Add any additional notes about the payment"
            ></textarea>
          </div>
          
          <div className="form-footer">
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Receipt'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
